import { Fragment, useId, useState } from 'react';

import { MeasurementSeriesStatusMap } from '@restDataAccess';
import { ScaleColor, Typography } from '@scaleaq/scaleui';

import { Icon, responsive } from '@/utils';

import { DataRow, DataRowDivider } from '../../dataRow';
import {
  StyledBreakdown,
  StyledChevron,
  StyledToggleButton,
  StyledToggleLabel,
} from './adultFemaleRow/adultFemaleRow.styles.ts';

type LiceRow = {
  label: string;
  value: number | undefined;
  status: MeasurementSeriesStatusMap[string][string] | undefined;
  color: ScaleColor | undefined;
};

type AdultFemaleRowProps = {
  female: LiceRow;
  /** Shown in a collapsible, indented panel under the Adult Female row. */
  breakdown: LiceRow[];
};

/** Adult Female row that expands to its egg-string split. Collapsed by default. */
export const AdultFemaleRow = ({ female, breakdown }: AdultFemaleRowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const buttonId = useId();
  const panelId = useId();

  return (
    <>
      <StyledToggleButton
        type='button'
        id={buttonId}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={() => setIsExpanded(expanded => !expanded)}
      >
        <DataRow
          label={
            <StyledToggleLabel>
              <Typography type='body_medium'>{female.label}</Typography>
              <StyledChevron $expanded={isExpanded} aria-hidden>
                <Icon.ChevronDown />
              </StyledChevron>
            </StyledToggleLabel>
          }
          value={female.value}
          isError={female.status?.isError ?? false}
          isLoading={female.status?.isLoading ?? false}
          color={female.color}
        />
      </StyledToggleButton>
      {isExpanded && (
        <StyledBreakdown direction='column' id={panelId} role='region' aria-labelledby={buttonId}>
          {breakdown.map(row => (
            <Fragment key={row.label}>
              <DataRowDivider spacing={responsive.spacing.small} />
              <DataRow
                compact
                label={row.label}
                labelType='body_small'
                value={row.value}
                isError={row.status?.isError ?? false}
                isLoading={row.status?.isLoading ?? false}
                color={row.color}
              />
            </Fragment>
          ))}
        </StyledBreakdown>
      )}
    </>
  );
};
